import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Joyride, EVENTS, ACTIONS, STATUS } from 'react-joyride';

// ── Tour steps ────────────────────────────────────────────────────────────────
// Targets are added by ID to key elements inside AIChatWidget:
//   #pgc-chat-fab          → the green FAB button (visible when chat is closed)
//   #pgc-chat-input-area   → the text input box  (visible when chat is open)
//   #pgc-chat-messages     → the messages area   (visible when chat is open)

const CHATBOT_TOUR_STEPS = [
  {
    key: 'chat-fab',
    target: '#pgc-chat-fab',
    title: 'AI Agronomist 🌱',
    content:
      'Ini adalah asisten AI agronomi PGC kamu. Tombol ini selalu tersedia di pojok kanan bawah setiap halaman. Klik "Berikutnya" untuk melihat cara menggunakannya!',
    placement: 'top-start',
    disableBeacon: true,
  },
  {
    key: 'chat-input',
    target: '#pgc-chat-input-area',
    title: 'Ajukan Pertanyaan Tanamanmu',
    content:
      'Ketik pertanyaan tentang tanamanmu di sini. Contoh: "I\'m growing lettuce, are conditions okay?" atau "cabe stage vegetatif, kondisi gimana?" Tekan Enter atau klik tombol kirim.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    key: 'chat-messages',
    target: '#pgc-chat-messages',
    title: 'Badge Sumber Data',
    content: (
      <span>
        Setiap jawaban AI dilengkapi badge sumber:
        <br />
        <br />
        <strong>✓ Verified Database</strong> — dari basis data agronomi terverifikasi oleh tim
        peneliti.
        <br />
        <strong>⚠ AI Generated</strong> — estimasi oleh LLM, gunakan sebagai referensi awal.
        <br />
        <br />
        Perhatikan selalu badge ini untuk menilai tingkat kepercayaan jawaban!
      </span>
    ),
    placement: 'right',
    disableBeacon: true,
  },
];

const USER_TOUR_STEPS = [
  {
    key: 'nav-access',
    target: '#pgc-nav-access-link',
    title: 'Mulai dari Tab Access',
    content:
      'Untuk akun baru, langkah pertama adalah membuka tab Access agar kamu bisa meminta izin menggunakan chamber PGC yang tersedia.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    key: 'request-access',
    target: '#pgc-access-request-section',
    title: 'Request Akses Chamber',
    content:
      'Di halaman ini, pilih chamber PGC yang ingin kamu gunakan lalu klik Request. Setelah admin menyetujui permintaanmu, chamber tersebut akan muncul di dashboard.',
    placement: 'top',
    disableBeacon: true,
  },
  ...CHATBOT_TOUR_STEPS,
];

// ── Component ─────────────────────────────────────────────────────────────────
// Props:
//   run      {boolean}  — set true from parent to launch the tour
//   onClose  {function} — called when tour ends or is skipped

export default function AgronomistTour({ run, onClose, variant }) {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [internalRun, setInternalRun] = useState(false);
  // Ref to track delayed step-change timers so we can cancel stale ones
  const stepTimerRef = useRef(null);
  const steps = useMemo(() => (variant === 'user' ? USER_TOUR_STEPS : CHATBOT_TOUR_STEPS), [variant]);

  const queueStepChange = useCallback((nextIndex, delay = 200) => {
    if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
    stepTimerRef.current = setTimeout(() => setStepIndex(nextIndex), delay);
  }, []);

  // Cleanup any pending step timer on unmount
  useEffect(() => () => { if (stepTimerRef.current) clearTimeout(stepTimerRef.current); }, []);

  // When parent sets run=true: close widget (so FAB is visible), then start tour
  useEffect(() => {
    if (!run) {
      return undefined;
    }

    window.dispatchEvent(new CustomEvent('pgc-tour-start')); // signals widget to close
    setStepIndex(0);
    const t = setTimeout(() => setInternalRun(true), 150); // wait for FAB to render
    return () => clearTimeout(t);
  }, [run]);

  const handleTourEvent = useCallback(
    (data) => {
      const { action, index, status, type } = data;
      const currentStep = steps[index];
      const currentStepKey = currentStep?.key;

      if (type === EVENTS.STEP_AFTER) {
        if (currentStepKey === 'nav-access' && action === ACTIONS.NEXT) {
          navigate('/dashboard/access');
          queueStepChange(index + 1, 250);
          return;
        }

        if (currentStepKey === 'chat-fab' && action === ACTIONS.NEXT) {
          // Advancing from FAB step → open widget before rendering step 1
          window.dispatchEvent(new CustomEvent('pgc-tour-open-widget'));
          queueStepChange(index + 1, 200); // wait for widget to render
          return;
        }

        if (currentStepKey === 'chat-input' && action === ACTIONS.PREV) {
          // Going back to FAB step → close widget so FAB is visible
          window.dispatchEvent(new CustomEvent('pgc-tour-start'));
          queueStepChange(index - 1, 200);
          return;
        }

        // All other step transitions
        setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
      }

      if (type === EVENTS.TARGET_NOT_FOUND) {
        if (currentStepKey === 'nav-access' && action !== ACTIONS.PREV) {
          navigate('/dashboard/access');
          queueStepChange(index + 1, 250);
          return;
        }
        // Respect navigation direction so backward nav doesn't jump forward
        setStepIndex((prev) => prev + (action === ACTIONS.PREV ? -1 : 1));
      }

      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
        setInternalRun(false);
        setStepIndex(0);
        onClose();
      }
    },
    [navigate, onClose, queueStepChange, steps]
  );

  if (!run && !internalRun) return null;

  return (
    <Joyride
      steps={steps}
      run={internalRun}
      stepIndex={stepIndex}
      onEvent={handleTourEvent}
      continuous
      locale={{
        back: 'Kembali',
        close: 'Tutup',
        last: 'Selesai',
        next: 'Berikutnya',
        skip: 'Lewati',
        open: 'Buka panduan',
      }}
      options={{
        primaryColor: '#2e7d32',
        zIndex: 10000,
        skipScroll: true,
        showProgress: true,
        buttons: ['back', 'close', 'primary', 'skip'],
      }}
      styles={{
        tooltip: {
          textAlign: 'left',
        },
      }}
    />
  );
}

AgronomistTour.propTypes = {
  onClose: PropTypes.func.isRequired,
  run: PropTypes.bool.isRequired,
  variant: PropTypes.oneOf(['admin', 'user']),
};

AgronomistTour.defaultProps = {
  variant: 'user',
};
